// Title derivation for occurrence rows in the meetings table. Child
// rows surface the occurrence period (e.g. "Apr 2026" for monthly,
// "Q2 2026" for quarterly) rather than repeating the parent's name,
// so the grouped view scans as a timeline instead of duplicate text.
// Differs from the deliverable equivalent: weekly cadences here render
// the full date ("1 Apr 2026") rather than "Wk of …" because users
// reference meetings by date in minutes/notes.

import type { Meeting } from "../../../../types";

function parseIsoUtc(iso: string): Date | null {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMonthYear(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return d.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatFullDate(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatQuarter(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  const month = d.getUTCMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter} ${d.getUTCFullYear()}`;
}

function formatYear(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return String(d.getUTCFullYear());
}

export function deriveMeetingOccurrenceTitle(
  row: Meeting,
  parent?: Meeting | null,
): string {
  if (!row.parentId) return row.name;
  const periodDate = row.occurrenceDate ?? row.date;
  if (!periodDate) return row.name;

  const frequency = parent?.recurrenceRule?.frequency;
  switch (frequency) {
    case "daily":
    case "weekly":
      return formatFullDate(periodDate) ?? row.name;
    case "quarterly":
      return formatQuarter(periodDate) ?? row.name;
    case "annually":
      return formatYear(periodDate) ?? row.name;
    case "monthly":
    default:
      return formatMonthYear(periodDate) ?? row.name;
  }
}
